import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/concepts/api-gateway')({
  component: APIGatewayPage,
  head: () => ({
    meta: [
      { title: 'What is an API Gateway? | Matricks' },
      { name: 'description', content: 'Learn what an API gateway is, how it works, and why it\'s essential for modern application architecture. Complete guide to API gateway concepts and benefits.' },
    ],
  }),
});

const benefits = [
  {
    iconName: "Shield",
    title: "Centralized Security",
    description: "Implement authentication, authorization, and threat protection in one place instead of across every service.",
  },
  {
    iconName: "Zap",
    title: "Performance Optimization",
    description: "Add caching, rate limiting, and load balancing to improve API performance and reliability.",
  },
  {
    iconName: "BarChart3",
    title: "Analytics & Monitoring",
    description: "Gain visibility into API usage, performance metrics, and error rates across all your services.",
  },
  {
    iconName: "Lock",
    title: "Access Control",
    description: "Define and enforce policies for who can access which APIs and what operations they can perform.",
  },
  {
    iconName: "Globe",
    title: "Protocol Translation",
    description: "Convert between protocols like REST, GraphQL, gRPC, and WebSocket seamlessly.",
  },
  {
    iconName: "Server",
    title: "Service Abstraction",
    description: "Hide internal service complexity from API consumers, allowing backend changes without client impact.",
  },
];

const useCases = [
  "Microservices architectures where you need to aggregate multiple backend services",
  "Mobile and web applications requiring a single entry point for API calls",
  "Partner and third-party integrations with different authentication requirements",
  "Legacy system modernization by exposing unified APIs",
  "Multi-cloud deployments requiring consistent API management",
];

const faqItems = [
  {
    question: "What's the difference between an API gateway and a reverse proxy?",
    answer: "While both sit between clients and backend services, an API gateway offers higher-level functionality like authentication, rate limiting, API versioning, and analytics. A reverse proxy primarily handles load balancing and basic routing. An API gateway is essentially a more sophisticated reverse proxy designed specifically for API management.",
  },
  {
    question: "Do I need an API gateway for a small application?",
    answer: "For small applications with few APIs, an API gateway might be overkill. However, as your application grows or if you're planning for scale, implementing an API gateway early can save significant refactoring later. Consider your future needs when making this decision.",
  },
  {
    question: "How does an API gateway affect latency?",
    answer: "An API gateway adds a small amount of latency (typically 1-10ms) as requests pass through it. However, this is often offset by features like caching, connection pooling, and response compression that can actually improve overall response times.",
  },
  {
    question: "Can I use multiple API gateways?",
    answer: "Yes, it's common to use multiple API gateways for different purposes. For example, you might have one for external APIs (partner/public) and another for internal microservice communication. This is known as a multi-gateway or federated gateway architecture.",
  },
  {
    question: "What's the difference between API gateway and API management?",
    answer: "An API gateway is a runtime component that handles request routing and policies. API management is a broader discipline that includes the gateway plus developer portals, API design tools, lifecycle management, and business analytics. Think of the gateway as one component of a complete API management solution.",
  },
];

function APIGatewayPage() {
  return (
    <div>
      <ContentHeader
        title="What is an API Gateway?"
        description="An API gateway is a server that acts as the single entry point for all API requests. It sits between clients and your backend services, providing a unified interface for routing, security, and management."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Concepts", href: "/concepts" },
          { label: "API Gateway" },
        ]}
        badge="Concept"
      />

      {/* What is it */}
      <ContentSection id="what-is-api-gateway" title="Understanding API Gateways">
        <div className="prose prose-gray dark:prose-invert max-w-none">
          <p className="text-lg text-muted-foreground leading-relaxed">
            An API gateway is a critical piece of infrastructure that manages all the traffic flowing
            between clients and your backend services. Think of it as a smart traffic controller that
            not only directs requests to the right service but also enforces security policies, handles
            authentication, and provides observability into your API ecosystem.
          </p>

          <p className="text-muted-foreground leading-relaxed mt-4">
            In a microservices architecture, instead of having clients communicate directly with dozens
            of individual services, they interact with a single API gateway. The gateway then routes
            requests to the appropriate backend service, aggregates responses when needed, and handles
            cross-cutting concerns like authentication and rate limiting.
          </p>

          <h3 className="text-xl font-semibold mt-8 mb-4">How It Works</h3>
          <div className="rounded-xl border bg-muted/30 p-6 not-prose">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center">
                <div className="h-16 w-16 mx-auto rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <span className="text-2xl">📱</span>
                </div>
                <p className="mt-2 text-sm font-medium">Client Apps</p>
              </div>
              <div className="text-muted-foreground">→</div>
              <div className="text-center">
                <div className="h-16 w-16 mx-auto rounded-lg bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl">🚪</span>
                </div>
                <p className="mt-2 text-sm font-medium">API Gateway</p>
                <p className="text-xs text-muted-foreground">Auth, Routing, Policies</p>
              </div>
              <div className="text-muted-foreground">→</div>
              <div className="text-center">
                <div className="h-16 w-16 mx-auto rounded-lg bg-green-500/10 flex items-center justify-center">
                  <span className="text-2xl">⚙️</span>
                </div>
                <p className="mt-2 text-sm font-medium">Backend Services</p>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      {/* Benefits */}
      <ContentSection
        id="benefits"
        title="Key Benefits"
        description="API gateways provide numerous advantages for modern application architecture."
        background="muted"
      >
        <FeatureShowcase features={benefits} columns={3} variant="cards" />
      </ContentSection>

      {/* Use Cases */}
      <ContentSection
        id="use-cases"
        title="Common Use Cases"
        description="API gateways are valuable in many scenarios."
      >
        <ul className="space-y-4">
          {useCases.map((useCase, index) => (
            <li key={index} className="flex items-start gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-medium flex-shrink-0">
                {index + 1}
              </div>
              <span className="text-muted-foreground">{useCase}</span>
            </li>
          ))}
        </ul>
      </ContentSection>

      {/* Related Concepts */}
      <ContentSection id="related" title="Related Concepts" background="muted">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/concepts/api-management"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">API Management</h4>
            <p className="text-sm text-muted-foreground mt-1">
              The broader discipline of API lifecycle management
            </p>
          </Link>
          <Link
            to="/concepts/zero-trust-security"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">Zero-Trust Security</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Security model that works with API gateways
            </p>
          </Link>
          <Link
            to="/concepts/rbac-abac"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">RBAC vs ABAC</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Access control models for API security
            </p>
          </Link>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Ready to implement an API Gateway?"
        description="Matricks provides a powerful API gateway with zero-trust security, 2000+ integrations, and enterprise-grade features."
        primaryAction={{ label: "Get Started Free", href: "/login" }}
        secondaryAction={{ label: "View Features", href: "/features" }}
        background="primary"
      />
    </div>
  );
}
